import type { ProcessingStatus } from "@/application/events/types";

interface EventProcessingBadgeProps {
  status: ProcessingStatus | null;
}

const STATUS_CONFIG: Record<string, { label: string; classes: string }> = {
  success: {
    label: "Success",
    classes: "bg-green-100 text-green-700",
  },
  failed: {
    label: "Failed",
    classes: "bg-red-100 text-red-700",
  },
  pending: {
    label: "Pending",
    classes: "bg-amber-100 text-amber-700",
  },
  unhandled: {
    label: "Unhandled",
    classes: "bg-muted text-muted-foreground",
  },
  // Store rows only, and both terminal rather than failures worth chasing:
  // `skipped` is a notification for another deployment under Pub/Sub fan-out,
  // and `parse_error` a body that will never parse. Falling through to
  // "Unknown" made them look like rows nobody had processed yet.
  skipped: {
    label: "Skipped",
    classes: "bg-muted text-muted-foreground",
  },
  parse_error: {
    label: "Parse error",
    classes: "bg-orange-100 text-orange-700",
  },
  // Terminal like the two above, but the only one worth counting: outside of
  // testing, one store account attaching one subscription to two businesses is
  // a person to look at rather than a row to ignore. Coloured accordingly.
  rejected_binding: {
    label: "Rejected binding",
    classes: "bg-red-100 text-red-700",
  },
  unknown: {
    label: "Unknown",
    classes: "bg-muted text-muted-foreground",
  },
};

export function EventProcessingBadge({ status }: EventProcessingBadgeProps) {
  const key = status ?? "unknown";
  // eslint-disable-next-line security/detect-object-injection -- controlled enum values
  const config = STATUS_CONFIG[key] ?? STATUS_CONFIG.unknown;

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${config.classes}`}
    >
      {config.label}
    </span>
  );
}
